import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useParams, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Welcome from './components/Welcome';
import Login from './components/Login';
import EmailConfirmation from './components/EmailConfirmation';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import { api, endpoints } from './config/api';
import './index.css';
import AuthCallback from './components/AuthCallback';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Cookies from './components/Cookies';
// SubscriptionCheck moved to lazy loading
import CookieConsent from './components/CookieConsent';
import NotFound from './components/NotFound';
import { Helmet } from 'react-helmet-async';
import { SpeedInsights } from "@vercel/speed-insights/react";
import LanguageIconSprite from './components/LanguageIconSprite';
import { SITE_ORIGIN } from './config/site';

// Lazy load heavy components not needed for initial page load
const Wizard = lazy(() => import('./components/Wizard'));
const TaskManager = lazy(() => import('./components/TaskManager'));
const TaskDetail = lazy(() => import('./components/TaskDetail'));
const Feedback = lazy(() => import('./components/Feedback'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const SetupPassword = lazy(() => import('./components/SetupPassword'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Support = lazy(() => import('./components/Support'));
const CostCalculator = lazy(() => import('./components/CostCalculator'));
const Guides = lazy(() => import('./components/Guides'));
const ArticleDetail = lazy(() => import('./components/ArticleDetail'));
const ChatBubble = lazy(() => import('./components/ChatBubble'));
const SubscriptionCheck = lazy(() => import('./components/SubscriptionCheck'));
const Tools = lazy(() => import('./components/Tools'));
const ToolDetail = lazy(() => import('./components/ToolDetail'));
const Hub = lazy(() => import('./components/Hub'));
const HelpSpain = lazy(() => import('./components/HelpSpain'));

// Wrapper component to force re-render when toolSlug changes
const ToolDetailWrapper = () => {
  const { toolSlug } = useParams();
  return <ToolDetail />;
};

// Wrapper component to force re-render when articleSlug changes
const ArticleDetailWrapper = () => {
  const { articleSlug } = useParams();
  return <ArticleDetail />;
};

// Lazy load admin components
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const CostManagement = lazy(() => import('./components/admin/CostManagement'));
const InsightsManagement = lazy(() => import('./components/admin/InsightsManagement'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));

// Loading fallback component for lazy loaded routes
const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-screen bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
  </div>
);

// Admin loading component
const AdminLoadingFallback = () => (
  <div className="flex justify-center items-center min-h-screen bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
  </div>
);

// Wrap admin routes in Suspense
const AdminRoutes = () => (
  <Suspense fallback={<AdminLoadingFallback />}>
    <Routes>
      <Route path="/" element={<AdminDashboard />} />
      <Route path="/cost-management" element={<CostManagement />} />
      <Route path="/insights" element={<InsightsManagement />} />
      <Route path="/users" element={<UserManagement />} />
    </Routes>
  </Suspense>
);

// Add PrivateAdminRoute component
const PrivateAdminRoute = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;

        const checkAdminStatus = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    //console.log('No token found, redirecting to login');
                    navigate('/login');
                    return;
                }

                // Use the api instance instead of axios directly
                const response = await api.get(endpoints.admin.status);

                if (!mounted) return;

                //console.log('Admin status response:', response.data);

                if (response.data.success && response.data.isAdmin) {
                    //console.log('User confirmed as admin');
                    setIsAdmin(true);
                } else {
                    //console.log('User is not admin, redirecting...');
                    navigate('/task-manager');
                }
            } catch (error) {
                if (!mounted) return;
                console.error('Admin check failed:', error.response?.data || error.message);
                setError(error.response?.data?.message || error.message);
                navigate('/task-manager');
            } finally {
                if (mounted) setLoading(false);
            }
        };

        checkAdminStatus();
        return () => { mounted = false; };
    }, [navigate]);

    if (loading) {
        return <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>;
    }

    if (error) {
        return <div className="text-red-500 text-center p-4">Error: {error}</div>;
    }

    return isAdmin ? children : null;
};

// Add this helper function at the component level
const RedirectToGuides = () => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Ensure we have an articleSlug before redirecting
    if (articleSlug) {
      navigate(`/guides/${articleSlug}`, { replace: true });
    }
  }, [articleSlug, navigate]);

  // Return null or a loading state while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  );
};

// External redirect component for agency-solutions
const RedirectToAgencySolutions = () => {
  useEffect(() => {
    // Permanent redirect (301) to external URL
    window.location.replace('https://crm.livelovespain.com');
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  );
};

// External redirect component for /crm paths
const RedirectToCRM = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Extract the path after /crm and redirect to crm.livelovespain.com
    const pathAfterCrm = pathname.replace(/^\/crm\/?/, '');
    const destination = pathAfterCrm 
      ? `https://crm.livelovespain.com/${pathAfterCrm}`
      : 'https://crm.livelovespain.com';
    window.location.replace(destination);
  }, [pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  );
};

// Component that uses useLocation inside Router context
const AppContent = () => {
    const { pathname } = useLocation();
    const { currentLanguage } = useLanguage();
    
    useEffect(() => {
    }, [pathname, currentLanguage]);
    const [userId, setUserId] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [subscriptionProcessing, setSubscriptionProcessing] = useState(false);

    // Immediate cleanup on route change
    useEffect(() => {
        const currentPageUrl = window.location.href;
        const currentPath = window.location.pathname;
        const isToolDetail = /^\/(?:[a-z]{2}\/)?tools\//.test(currentPath);
        // On Tool Details pages, avoid interfering with Helmet-managed head tags to prevent DOM removal races
        if (isToolDetail) {
            return;
        }
        
        // Remove ALL JSON-LD scripts that aren't for the current page (including Helmet ones)
        document.querySelectorAll('script[type="application/ld+json"]:not([data-rh="true"])').forEach((n) => {
            const scriptPage = n.getAttribute('data-page');
            if (!scriptPage || scriptPage === 'global') return; // Keep global schemas
            
            // Check if this script belongs to the current page
            const isCurrentPage = scriptPage === currentPageUrl || 
                                scriptPage.endsWith(currentPath) ||
                                scriptPage.endsWith(currentPath + '/');
            
            if (!isCurrentPage) {
                n.parentNode?.removeChild(n);
            }
        });
        
        // Also remove any scripts with wrong data-page attributes immediately
        document.querySelectorAll('script[type="application/ld+json"][data-page*="guides/"]:not([data-rh="true"])').forEach((n) => {
            if (!currentPath.includes('/guides/')) {
                n.parentNode?.removeChild(n);
            }
        });
        
        // Remove article schemas when navigating to non-article pages
        document.querySelectorAll('script[type="application/ld+json"][data-component="article"]:not([data-rh="true"])').forEach((n) => {
            if (!currentPath.includes('/guides/')) {
                n.parentNode?.removeChild(n);
            }
        });
        
        // Remove article-specific meta tags when navigating to non-article pages
        if (!currentPath.includes('/guides/')) {
            const articleMetaSelectors = [
                'meta[property="article:published_time"]',
                'meta[property="article:modified_time"]',
                'meta[property="og:updated_time"]',
                'meta[property="article:author"]',
                'meta[property="article:section"]',
                'meta[property="article:tag"]',
                'meta[property="og:image:secure_url"]',
                'meta[property="og:image:width"]',
                'meta[property="og:image:height"]',
                'meta[property="og:image:alt"]',
                'meta[property="og:image:type"]',
                'meta[name="twitter:image:alt"]'
            ];
            
            articleMetaSelectors.forEach(selector => {
                document.querySelectorAll(selector).forEach(meta => {
                    meta.remove();
                });
            });
        }
        
        // Remove hreflang links that point to guides when navigating to non-guide pages
        if (!currentPath.includes('/guides/')) {
            // Remove any hreflang links pointing to guides
            document.querySelectorAll('link[hreflang][href*="/guides/"]').forEach((n) => {
                n.remove();
            });
            
            // Also remove any hreflang links that don't match the current page type
            document.querySelectorAll('link[hreflang]:not([data-rh="true"])').forEach((n) => {
                const href = n.getAttribute('href');
                if (!href) return;
                
                // If this is a cost-explorer page, remove any hreflang links that don't point to cost-explorer
                if (currentPath.includes('/cost-explorer') && !href.includes('/cost-explorer')) {
                    n.remove();
                }
            });
        }
        
        // Remove ALL hreflang links that aren't for the current page path
        document.querySelectorAll('link[hreflang]:not([data-rh="true"])').forEach((n) => {
            const href = n.getAttribute('href');
            if (!href) return;
            
            // Extract the path from the href (remove domain and language prefix)
            const url = new URL(href);
            let hrefPath = url.pathname.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
            let currentPathNormalized = currentPath.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
            
            // Special handling for root path - normalize both to '/'
            if (hrefPath === '/' || hrefPath === '') hrefPath = '/';
            if (currentPathNormalized === '/' || currentPathNormalized === '') currentPathNormalized = '/';
            
            // Check if this hreflang is for the current page path (ignoring language)
            const isCurrentPage = hrefPath === currentPathNormalized || 
                                hrefPath === currentPathNormalized + '/' ||
                                hrefPath === currentPathNormalized.replace(/\/$/, '');
            
            if (!isCurrentPage) {
                n.parentNode?.removeChild(n);
            }
        });
        
        // Remove ALL preload links for images to prevent asset bleeding
        document
            .querySelectorAll('link[rel="preload"][as="image"]')
            .forEach((n) => n.parentNode?.removeChild(n));
        
        // Remove ALL canonical links that aren't for the current page
        document.querySelectorAll('link[rel="canonical"]:not([data-rh="true"])').forEach((n) => {
            const href = n.getAttribute('href');
            if (!href) return;
            
            // Check if this canonical is for the current page
            const isCurrentPage = href === currentPageUrl || 
                                href.endsWith(currentPath) ||
                                href.endsWith(currentPath + '/');
            
            if (!isCurrentPage) {
                n.parentNode?.removeChild(n);
            }
        });
    }, [pathname]);

    // Flush effect to clean up stale Helmet-managed JSON-LD scripts from previous pages
    useEffect(() => {
        // Skip aggressive cleanup on Tool Details to avoid race with Helmet
        const isToolDetail = /^\/(?:[a-z]{2}\/)?tools\//.test(window.location.pathname);
        if (isToolDetail) return;

        // Add a small delay to ensure all components have mounted and added their JSON-LD
        const timeoutId = setTimeout(() => {
            // Remove ALL JSON-LD scripts that aren't for the current page (including Helmet ones)
            const currentPageUrl = window.location.href;
            const currentPath = window.location.pathname;
            
            document.querySelectorAll('script[type="application/ld+json"]:not([data-rh="true"])').forEach((n) => {
                const scriptPage = n.getAttribute('data-page');
                if (!scriptPage || scriptPage === 'global') return; // Keep scripts without data-page (global) and global schemas
                
                // Check if this script belongs to the current page
                const isCurrentPage = scriptPage === currentPageUrl || 
                                    scriptPage.endsWith(currentPath) ||
                                    scriptPage.endsWith(currentPath + '/');
                
                if (!isCurrentPage) {
                    n.parentNode?.removeChild(n);
                }
            });

            // Remove ALL hreflang links that aren't for the current page path
            // This includes both Helmet and non-Helmet links to prevent cross-contamination
            document.querySelectorAll('link[hreflang]:not([data-rh="true"])').forEach((n) => {
                const href = n.getAttribute('href');
                if (!href) return;
                
                // Extract the path from the href (remove domain and language prefix)
                const url = new URL(href);
                let hrefPath = url.pathname.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
                let currentPathNormalized = currentPath.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
                
                // Special handling for root path - normalize both to '/'
                if (hrefPath === '/' || hrefPath === '') hrefPath = '/';
                if (currentPathNormalized === '/' || currentPathNormalized === '') currentPathNormalized = '/';
                
                // Check if this hreflang is for the current page path (ignoring language)
                const isCurrentPage = hrefPath === currentPathNormalized || 
                                    hrefPath === currentPathNormalized + '/' ||
                                    hrefPath === currentPathNormalized.replace(/\/$/, '');
                
                if (!isCurrentPage) {
                    n.parentNode?.removeChild(n);
                }
            });
            
            document.querySelectorAll('link[rel="canonical"]:not([data-rh="true"])').forEach((n) => {
                const href = n.getAttribute('href');
                if (!href) return;
                
                // Check if this canonical is for the current page
                const isCurrentPage = href === currentPageUrl || 
                                    href.endsWith(currentPath) ||
                                    href.endsWith(currentPath + '/');
                
                if (!isCurrentPage) {
                    n.parentNode?.removeChild(n);
                }
            });
        }, 100); // Small delay to ensure components have mounted

        return () => clearTimeout(timeoutId);
    }, [pathname]);

    // Additional cleanup effect - remove any remaining stale elements
    useEffect(() => {
        const isToolDetail = /^\/(?:[a-z]{2}\/)?tools\//.test(window.location.pathname);
        if (isToolDetail) return;

        const cleanupTimeoutId = setTimeout(() => {
            // Remove ALL image preloads to prevent stale preloads
            document
                .querySelectorAll('link[rel="preload"][as="image"]')
                .forEach((n) => {
                    n.parentNode?.removeChild(n);
                });
            
            // Final cleanup of any remaining stale elements
            const currentPageUrl = window.location.href;
            const currentPath = window.location.pathname;
            
            // Remove any remaining non-Helmet JSON-LD scripts not for current page
            document.querySelectorAll('script[type="application/ld+json"]:not([data-rh="true"])').forEach((n) => {
                const scriptPage = n.getAttribute('data-page');
                if (!scriptPage || scriptPage === 'global') return; // Keep scripts without data-page (global) and global schemas
                
                const isCurrentPage = scriptPage === currentPageUrl || 
                                    scriptPage.endsWith(currentPath) ||
                                    scriptPage.endsWith(currentPath + '/');
                
                if (!isCurrentPage) {
                    n.parentNode?.removeChild(n);
                }
            });
            
            // Final cleanup: Remove any article schemas that might have persisted
            if (!currentPath.includes('/guides/')) {
                document.querySelectorAll('script[type="application/ld+json"][data-component="article"]').forEach((n) => {
                    n.remove();
                });
                
                // Remove any remaining article meta tags
                const articleMetaSelectors = [
                    'meta[property="article:published_time"]',
                    'meta[property="article:modified_time"]',
                    'meta[property="og:updated_time"]',
                    'meta[property="article:author"]',
                    'meta[property="article:section"]',
                    'meta[property="article:tag"]'
                ];
                
                articleMetaSelectors.forEach(selector => {
                    document.querySelectorAll(selector).forEach(meta => {
                        meta.remove();
                    });
                });
            }
            
            // Remove any remaining non-Helmet hreflang links not for current page path
            // But preserve all language variants of the current page
            document.querySelectorAll('link[hreflang]:not([data-rh="true"])').forEach((n) => {
                const href = n.getAttribute('href');
                if (!href) return;
                
                // Extract the path from the href (remove domain and language prefix)
                const url = new URL(href);
                let hrefPath = url.pathname.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
                let currentPathNormalized = currentPath.replace(/^\/[a-z]{2}\//, '/'); // Remove language prefix
                
                // Special handling for root path - normalize both to '/'
                if (hrefPath === '/' || hrefPath === '') hrefPath = '/';
                if (currentPathNormalized === '/' || currentPathNormalized === '') currentPathNormalized = '/';
                
                // Check if this hreflang is for the current page path (ignoring language)
                const isCurrentPage = hrefPath === currentPathNormalized || 
                                    hrefPath === currentPathNormalized + '/' ||
                                    hrefPath === currentPathNormalized.replace(/\/$/, '');
                
                if (!isCurrentPage) {
                    n.parentNode?.removeChild(n);
                }
            });
            
            // Final cleanup: Remove any guide hreflang links when on non-guide pages
            if (!currentPath.includes('/guides/')) {
                document.querySelectorAll('link[hreflang][href*="/guides/"]').forEach((n) => {
                    n.remove();
                });
            }
        }, 500); // Longer delay for late-mounted components

        return () => clearTimeout(cleanupTimeoutId);
    }, [pathname]);

    // Initialize Google Consent and AdSense flags before any components mount
    useEffect(() => {
        // Initialize global tracking flags ONLY if not already set
        if (window.adsenseAutoAdsInitialized === undefined) {
            window.adsenseAutoAdsInitialized = false;
        }
        if (window.adsenseScriptLoaded === undefined) {
            window.adsenseScriptLoaded = false;
        }
        if (window.adsenseLoading === undefined) {
            window.adsenseLoading = false;
        }
        
        // Initialize global AdSense flags (consent management handled in index.html)
        window.adsenseAutoAdsInitialized = false;
        window.adsenseScriptLoaded = false;
        window.adsenseLoading = false;
        
        //console.log('LiveLoveSpain: Global flags initialized');
    }, []);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            // Clean up only non-Helmet dynamic content when component unmounts
            document
                .querySelectorAll('script[type="application/ld+json"]:not([data-rh="true"])')
                .forEach((n) => n.parentNode?.removeChild(n));
            
            document
                .querySelectorAll('link[hreflang]:not([data-rh="true"])')
                .forEach((n) => n.parentNode?.removeChild(n));
            
            document
                .querySelectorAll('link[rel="preload"][as="image"]')
                .forEach((n) => n.parentNode?.removeChild(n));
            
            document
                .querySelectorAll('link[rel="canonical"]:not([data-rh="true"])')
                .forEach((n) => n.parentNode?.removeChild(n));
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            // Decode the token to get userId
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(window.atob(base64));
                setUserId(payload.userId);
            } catch (error) {
                //console.error('Error decoding token:', error);
            }
        }
    }, []);

    return (
        <>
        <ErrorBoundary>
            <Helmet 
                defaultTitle="LiveLoveSpain - Your App for Immigration & Life in Spain"
                titleTemplate="%s | LiveLoveSpain"
                prioritizeSeoTags={true}
                defer={false}
                htmlAttributes={{ lang: currentLanguage }}
            >
                {/* Global Meta Tags */}
                <link rel="icon" type="image/x-icon" href="/assets/LiveLoveSpain.ico" />
                <link rel="icon" type="image/png" sizes="96x96" href="/assets/LiveLoveSpain-96.png" />
                <link rel="apple-touch-icon" sizes="180x180" href="/assets/LiveLoveSpain-apple-touch.png" />
                <link rel="manifest" href="/manifest.json" />
                
                {/* Global Sitewide Knowledge Graph (Organization, WebSite, Hubs/CollectionPages) */}
                <script id="ld-global" type="application/ld+json" data-page="global">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "Organization",
                                "@id": `${SITE_ORIGIN}/#organization`,
                                "name": "LiveLoveSpain",
                                "url": `${SITE_ORIGIN}/`,
                                "logo": {
                                    "@type": "ImageObject",
                                    "url": `${SITE_ORIGIN}/assets/LiveLoveSpain-512.png`,
                                    "width": 512,
                                    "height": 512
                                },
                                "image": {
                                    "@type": "ImageObject",
                                    "url": `${SITE_ORIGIN}/assets/LiveLoveSpain-512.png`,
                                    "width": 512,
                                    "height": 512
                                },
                                "sameAs": [
                                    "https://www.facebook.com/people/Livelovespain/61572933601843/",
                                    "https://www.instagram.com/live.lovespain/"
                                ],
                                "contactPoint": {
                                    "@type": "ContactPoint",
                                    "contactType": "customer support",
                                    "email": "gerard@livelovespain.com"
                                }
                            },
                            {
                                "@type": "WebSite",
                                "@id": `${SITE_ORIGIN}/#website`,
                                "url": `${SITE_ORIGIN}/`,
                                "name": "LiveLoveSpain",
                                "publisher": { "@id": `${SITE_ORIGIN}/#organization` }
                            }
                        ]
                    })}
                </script>
            </Helmet>
            <Routes key={pathname}>
                <Route path="/" element={<Welcome />} />
                {/* Language-specific home routes (explicit to avoid shadowing hub routes) */}
                <Route path="/en" element={<Welcome />} />
                <Route path="/es" element={<Welcome />} />
                <Route path="/fr" element={<Welcome />} />
                <Route path="/de" element={<Welcome />} />
                <Route path="/pt" element={<Welcome />} />
                {/* Language-specific home route (placed after specific lang routes below) */}
                <Route 
                    path="/login" 
                    element={
                        <Login 
                            onLoginSuccess={(userId) => {
                                setUserId(userId);
                            }} 
                        />
                    } 
                />
                <Route 
                    path="/:lang/login" 
                    element={
                        <Login 
                            onLoginSuccess={(userId) => {
                                setUserId(userId);
                            }} 
                        />
                    } 
                />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/:lang/email-confirmation" element={<EmailConfirmation />} />
                <Route 
                    path="/wizard" 
                    element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingFallback />}>
                                <Wizard />
                            </Suspense>
                        </PrivateRoute>
                    } 
                />
                <Route 
                    path="/:lang/wizard" 
                    element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingFallback />}>
                                <Wizard />
                            </Suspense>
                        </PrivateRoute>
                    } 
                />
                <Route 
                    path="/task-manager" 
                    element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingFallback />}>
                                <TaskManager 
                                    userId={userId}
                                    onLogout={() => {
                                        setUserId(null);
                                        localStorage.removeItem('authToken');
                                    }}
                                    onRevisitWizard={() => {/* your wizard logic */}}
                                >
                                    <Suspense fallback={<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />}>
                                        <SubscriptionCheck />
                                    </Suspense>
                                </TaskManager>
                            </Suspense>
                        </PrivateRoute>
                    } 
                />
                <Route 
                    path="/:lang/task-manager" 
                    element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingFallback />}>
                                <TaskManager 
                                    userId={userId}
                                    onLogout={() => {
                                        setUserId(null);
                                        localStorage.removeItem('authToken');
                                    }}
                                >
                                    <Suspense fallback={<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />}>
                                        <SubscriptionCheck />
                                    </Suspense>
                                </TaskManager>
                            </Suspense>
                        </PrivateRoute>
                    } 
                />
                <Route path="/tasks/:taskId" element={
                    <PrivateRoute>
                        <Suspense fallback={<LoadingFallback />}>
                            <TaskDetail userId={userId} />
                        </Suspense>
                    </PrivateRoute>
                } />
                <Route path="/:lang/tasks/:taskId" element={
                    <PrivateRoute>
                        <Suspense fallback={<LoadingFallback />}>
                            <TaskDetail userId={userId} />
                        </Suspense>
                    </PrivateRoute>
                } />
                <Route path="/feedback" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Feedback />
                    </Suspense>
                } />
                <Route path="/:lang/feedback" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Feedback />
                    </Suspense>
                } />
                <Route 
                    path="/admin/*" 
                    element={
                        <PrivateAdminRoute>
                            <AdminRoutes />
                        </PrivateAdminRoute>
                    } 
                />
                <Route 
                    path="/:lang/admin/*" 
                    element={
                        <PrivateAdminRoute>
                            <AdminRoutes />
                        </PrivateAdminRoute>
                    } 
                />
                <Route path="/verify" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <EmailVerification />
                    </Suspense>
                } />
                <Route path="/:lang/verify" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <EmailVerification />
                    </Suspense>
                } />
                <Route path="/setup-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <SetupPassword />
                    </Suspense>
                } />
                <Route path="/:lang/setup-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <SetupPassword />
                    </Suspense>
                } />
                <Route path="/forgot-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ForgotPassword />
                    </Suspense>
                } />
                <Route path="/:lang/forgot-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ForgotPassword />
                    </Suspense>
                } />
                <Route path="/reset-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ResetPassword />
                    </Suspense>
                } />
                <Route path="/:lang/reset-password" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ResetPassword />
                    </Suspense>
                } />
                <Route 
                    path="/subscription/success" 
                    element={
                        <PrivateRoute>
                            <Navigate 
                                to="/task-manager" 
                                replace 
                                state={{ 
                                    showSubscriptionSuccess: true,
                                    sessionId: new URLSearchParams(window.location.search).get('session_id'),
                                    isProcessing: true
                                }} 
                            />
                        </PrivateRoute>
                    } 
                />
                <Route 
                    path="/subscription/cancel" 
                    element={
                        <PrivateRoute>
                            <Navigate 
                                to="/task-manager" 
                                replace 
                                state={{ 
                                    showSubscriptionError: true,
                                    error: "Subscription process was cancelled."
                                }} 
                            />
                        </PrivateRoute>
                    } 
                />
                <Route path="/support" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Support />
                    </Suspense>
                } />
                <Route path="/:lang/support" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Support />
                    </Suspense>
                } />
                <Route path="/guides" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Guides />
                    </Suspense>
                } />
                <Route path="/guides/:articleSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ArticleDetailWrapper />
                    </Suspense>
                } />
                {/* Language-specific routes */}
                <Route path="/:lang/guides" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Guides />
                    </Suspense>
                } />
                <Route path="/:lang/guides/:articleSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ArticleDetailWrapper />
                    </Suspense>
                } />
                <Route path="/agency-solutions" element={<RedirectToAgencySolutions />} />
                <Route path="/:lang/agency-solutions" element={<RedirectToAgencySolutions />} />
                <Route path="/crm" element={<RedirectToCRM />} />
                <Route path="/crm/*" element={<RedirectToCRM />} />
                <Route path="/tools" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Tools />
                    </Suspense>
                } />
                <Route path="/tools/:toolSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ToolDetailWrapper />
                    </Suspense>
                } />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/:lang/terms" element={<Terms />} />
                <Route path="/:lang/privacy" element={<Privacy />} />
                <Route path="/:lang/cookies" element={<Cookies />} />
                <Route path="/help-spain" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <HelpSpain />
                  </Suspense>
                } />
                <Route path="/cost-explorer/compare/:pair" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                <Route path="/cost-explorer/:citySlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                <Route path="/cost-explorer" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                {/* Language-specific Cost Explorer routes */}
                <Route path="/:lang/cost-explorer/compare/:pair" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                <Route path="/:lang/cost-explorer/:citySlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                <Route path="/:lang/cost-explorer" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <CostCalculator />
                    </Suspense>
                } />
                <Route 
                    path="/support/articles/:articleSlug" 
                    element={<RedirectToGuides />}
                />
                {/* Language-specific routes for tools */}
                <Route path="/:lang/tools" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Tools />
                    </Suspense>
                } />
                <Route path="/:lang/tools/:toolSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <ToolDetailWrapper />
                    </Suspense>
                } />
                {/* Language-specific Help Spain route (must be before generic hub route) */}
                <Route path="/:lang/help-spain" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <HelpSpain />
                    </Suspense>
                } />
                {/* Generic language-aware hub route */}
                <Route path="/:lang/:hubSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Hub />
                    </Suspense>
                } />
                {/* Legal Pages */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/:lang/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/:lang/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/:lang/cookies" element={<Cookies />} />
                {/* Hub Routes - must be after specific routes but before catch-all */}
                <Route path="/:hubSlug" element={
                    <Suspense fallback={<LoadingFallback />}>
                        <Hub />
                    </Suspense>
                } />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
            <SpeedInsights />
            <Suspense fallback={null}>
                <ChatBubble />
            </Suspense>
            {/* SVG sprite for language icons */}
            <LanguageIconSprite />
        </ErrorBoundary>
        </>
    );
};

function App() {
    return (
        <Router>
            <LanguageProvider>
                <AppContent />
            </LanguageProvider>
        </Router>
    );
}

export default App;
